/* eslint-disable react/no-array-index-key */
import { Collapse, Divider, Paper, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { currencies } from '../../Configurations/ExpenseEntryConfig';
import { ExpenseStatusValuesEnum } from './ExpenseStatus.enum';
import { getVaultExpensesById } from '../../Services/ExpenseService';
import { ExpenseModeEnum } from './ExpenseMode.enum';

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(3),
        marginTop: theme.spacing(4),
    },
    lineItem: {
        display: 'flex',
        justifyContent: 'space-between',

        '&:not(:last-child)': {
            marginBottom: theme.spacing(1),
        },
    },
    divider: {
        margin: theme.spacing(1, 0, 3, 0),
    },
    viewMore: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1, 0),

        '&:hover': {
            cursor: 'pointer',
        },
    },
    total: {
        marginTop: theme.spacing(2),
    },
    balanceZero: {
        color: 'green',
    },
    balanceBlack: {
        color: '#2a3953',
    },
    balanceOff: {
        color: 'red',
    },
}));

const getDisplayAmount = (expense, isReimbursement, mode) => {
    if (expense.status !== ExpenseStatusValuesEnum.Abandoned) {
        if (mode === 'cc') {
            const value = expense?.allocatedAmount;
            const numericValue = Number(value);
            return `$${Number.isFinite(numericValue) ? numericValue.toFixed(2) : '0.00'}`;
        }

        const amountValue = isReimbursement ? expense.reimbursedAmount : expense.amount;

        if (amountValue && amountValue !== '-') {
            return `$${parseFloat(amountValue).toFixed(2)}`;
        }
        return `$0`;
    }

    return null;
};

const ExpenseSummaryItem = ({ expense, isReimbursement, mode }) => (
    <>
        <Typography variant="body2">
            {expense.status !== ExpenseStatusValuesEnum.Abandoned ? expense.label : null}
        </Typography>
        <Typography variant="body2">{getDisplayAmount(expense, isReimbursement, mode)}</Typography>
    </>
);

const ExpenseSummary = ({
    mode,
    setError,
    clearErrors,
    setShowBalanceError,
    showBalanceError,
    setShowBalanceWarning,
    isReimbursement,
    isAnyApproved,
    expenseId,
}) => {
    const classes = useStyles();
    const { watch } = useFormContext();
    const expenseEntries = watch('expenseEntries');
    const limit = 2;
    const [viewMore, setViewMore] = useState(false);

    const [vaultTotalCharge, setVaultTotalCharge] = useState({});
    const isMounted = useRef(true);
    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);
    useEffect(() => {
        if (mode === ExpenseModeEnum.Vault && expenseId) {
            const fetchExpenseById = async () => {
                try {
                    const res = await getVaultExpensesById(expenseId);
                    if (isMounted.current) setVaultTotalCharge(res);
                } catch (e) {
                    console.log(e);
                }
            };
            fetchExpenseById();
        }
    }, [mode, expenseId]);

    const entriesTotal =
        Math.round(
            expenseEntries
                .filter(
                    (expense) =>
                        expense.status !== ExpenseStatusValuesEnum.Abandoned &&
                        (isReimbursement
                            ? expense.reimbursedAmount !== '-'
                            : mode === 'cc'
                            ? expense.allocatedAmount !== '-' && expense.allocatedAmount !== ''
                            : expense.amount !== '-') &&
                        (isReimbursement
                            ? expense.reimbursedAmount !== ''
                            : mode === 'cc'
                            ? expense.allocatedAmount !== '' &&
                              expense.allocatedAmount !== null &&
                              expense.allocatedAmount !== undefined
                            : expense.amount !== '')
                )
                .reduce((total, expense) => {
                    const value = isReimbursement
                        ? Number(expense.reimbursedAmount)
                        : mode === 'cc'
                        ? Number(expense.allocatedAmount)
                        : Number(expense.amount);
                    return total + (Number.isFinite(value) ? value : 0);
                }, 0) * 100
        ) / 100;

    const creditCardAmount = Number(expenseEntries?.[0]?.amount || 0);
    const totalAmountBase =
        mode === 'cc' && Number.isFinite(creditCardAmount)
            ? creditCardAmount
            : expenseEntries?.[0]?.totalAmount;

    const balance = Math.round(((Number(totalAmountBase) || 0) - entriesTotal) * 100) / 100;

    const hasBalanceError = (expenseEntry, totalOfEntries, balanceError) =>
        (totalOfEntries > expenseEntry.amount && expenseEntry.currency === 'USD') ||
        (totalOfEntries <= 0 && balanceError);

    const { id } = useParams();
    const currency = currencies.find((c) => c.value === expenseEntries[0].currency);
    const submittedAmount = !Number.isNaN(parseFloat(expenseEntries[0].amount))
        ? parseFloat(expenseEntries[0].amount).toFixed(2)
        : 0;
    useEffect(() => {
        if (mode === 'cc') {
            if (balance !== 0) {
                setError('balance', {
                    type: 'manual',
                    message:
                        'The sum of the expenses does not match the current transaction amount.',
                });
            } else {
                clearErrors('balance');
                setShowBalanceError(false);
            }
        }

        if (isReimbursement) {
            if (entriesTotal > expenseEntries[0].amount && expenseEntries[0].currency === 'USD') {
                if (isAnyApproved) {
                    setError('balance', {
                        type: 'manual',
                        message: 'The reimbursed amount exceeds the submitted amount.',
                    });
                    setShowBalanceError(true);
                    setShowBalanceWarning(false);
                } else {
                    clearErrors('balance');
                    setShowBalanceWarning(true);
                    setShowBalanceError(false);
                }
            } else if (entriesTotal <= 0 && isAnyApproved) {
                setError('balance', {
                    type: 'manual',
                    message:
                        'The reimbursed amount must be greater than zero to approve the expense.',
                });
                setShowBalanceWarning(false);
            } else {
                // Ensure all Errors/Warnings are cleared
                clearErrors('balance');
                setShowBalanceError(false);
                setShowBalanceWarning(false);
            }
        }
    }, [entriesTotal, isAnyApproved, currency, submittedAmount]);

    const getBalanceClass = () => {
        if (hasBalanceError(expenseEntries[0], entriesTotal, showBalanceError)) {
            return classes.balanceOff;
        }
        if (entriesTotal <= 0) {
            return classes.balanceBlack;
        }
        return classes.balanceZero;
    };

    const getTitle = () => {
        if (mode === ExpenseModeEnum.Vault) {
            return 'Charge Summary';
        }
        if (isReimbursement) {
            return 'Reimbursement Summary';
        }
        return 'Expense Summary';
    };

    const getTotal = () => {
        if (mode === ExpenseModeEnum.Vault) {
            return vaultTotalCharge.totalCharge ? vaultTotalCharge.totalCharge.toFixed(2) : 0;
        }

        if (mode === 'cc') {
            const amount = Number(expenseEntries?.[0]?.amount || 0);
            return Number.isFinite(amount) ? amount.toFixed(2) : 0;
        }
        return entriesTotal !== 0 ? entriesTotal.toFixed(2) : 0;
    };

    return (
        <Paper elevation={1} className={classes.paper}>
            <Typography variant="h5">{getTitle()}</Typography>

            <Divider className={classes.divider} />

            {mode !== 'vault' &&
                expenseEntries.slice(0, limit).map((expense, index) => (
                    <div className={classes.lineItem} key={`${index}-line-item`}>
                        <ExpenseSummaryItem
                            expense={expense}
                            isReimbursement={isReimbursement}
                            mode={mode}
                        />
                    </div>
                ))}
            <Collapse in={viewMore}>
                {expenseEntries.slice(limit).map((expense, index) => (
                    <div className={classes.lineItem} key={`${index}-line-item`}>
                        <ExpenseSummaryItem
                            expense={expense}
                            isReimbursement={isReimbursement}
                            mode={mode}
                        />
                    </div>
                ))}
            </Collapse>
            {expenseEntries.length > limit && (
                <Typography
                    variant="body2"
                    onClick={() => setViewMore((prev) => !prev)}
                    className={classes.viewMore}
                >
                    <b>{viewMore ? 'Show less' : 'View more'}</b>{' '}
                    <ExpandMoreIcon
                        style={{
                            transition: 'transform .25s',
                            transform: `${viewMore ? 'rotate(180deg)' : 'rotate(0)'}`,
                        }}
                        fontSize="small"
                    />
                </Typography>
            )}

            {isReimbursement ? (
                <div className={`${classes.lineItem} ${classes.total}`}>
                    <Typography className={getBalanceClass()} variant="h5">
                        Reimbursement Total
                    </Typography>
                    <Typography className={getBalanceClass()} variant="h5">
                        ${entriesTotal !== 0 ? entriesTotal.toFixed(2) : 0}
                    </Typography>
                </div>
            ) : (
                <div className={`${classes.lineItem} ${classes.total}`}>
                    <Typography variant="h5">Total</Typography>
                    <Typography variant="h5">${getTotal()} </Typography>
                </div>
            )}

            {isReimbursement && (
                <div className={`${classes.lineItem} ${classes.total}`}>
                    <Typography variant="h7">Submitted Expense Amount</Typography>
                    <Typography variant="h7">
                        {currency.symbol}
                        {submittedAmount}{' '}
                        {currency.symbol === '$' && currency.value !== 'USD'
                            ? ` ${currency.value}`
                            : ''}
                    </Typography>
                </div>
            )}
            {mode === 'cc' && (
                <div className={`${classes.lineItem} ${classes.total}`}>
                    <Typography
                        variant="h5"
                        className={balance !== 0 ? classes.balanceOff : classes.balanceZero}
                    >
                        Balance
                    </Typography>
                    <Typography
                        variant="h5"
                        className={balance !== 0 ? classes.balanceOff : classes.balanceZero}
                    >
                        ${balance !== 0 ? balance.toFixed(2) : 0}
                    </Typography>
                </div>
            )}
        </Paper>
    );
};

export default ExpenseSummary;
